import { DcuplUpdateType } from './changeDetection';
import { generateObjectHash } from './object-hash';
import { perf } from './performance';

export type AnalyticsEntryType = 'resource' | 'mark' | 'measure' | 'data' | 'sdk_action';

export type AnalyticsEntryBase<Value = any> = {
  name: string;
  type: AnalyticsEntryType;
  value: Value;
  createdAt: number;
};

export type AnalyticsEntryResource = AnalyticsEntryBase<AnalyticsEntryResourceValue> & {
  type: 'resource';
};
export type AnalyticsEntryMark = AnalyticsEntryBase<number> & {
  type: 'mark';
};

export type AnalyticsEntryMeasure = AnalyticsEntryBase<number> & {
  type: 'measure';
  detail?: any;
};

export type AnalyticsEntryData = AnalyticsEntryBase<any> & {
  type: 'data';
};

export type AnalyticsEntrySdkAction = AnalyticsEntryBase<AnalyticsEntrySdkActionValue> & {
  type: 'sdk_action';
};

export type AnalyticsEntry =
  | AnalyticsEntryResource
  | AnalyticsEntryMark
  | AnalyticsEntryMeasure
  | AnalyticsEntryData
  | AnalyticsEntrySdkAction;

export type AnalyticsEntrySdkActionValue = {
  duration: number;
  model?: string;
  args?: any;
};

export type AnalyticsEntryResourceValue = {
  model: string;
  type: 'data' | 'model';
  start: number;
  startTime: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
  end: number;
  duration: number;
  parseStart: number;
  parseEnd: number;
  rawTransformStart?: number;
  rawTransformEnd?: number;
  csvToJsonStart?: number;
  csvToJsonEnd?: number;
  parsedTransformStart?: number;
  parsedTransformEnd?: number;
  decodedBodySize: number;
  encodedBodySize: number;
};

export class AnalyticsController {
  private data!: Map<string, AnalyticsEntry>;

  private enabled = false;

  constructor() {}

  public init(enabled: boolean): void {
    this.data = new Map();
    this.enabled = enabled;
  }

  public set(options: { value: AnalyticsEntry; context?: any }): AnalyticsEntry | undefined {
    if (!this.enabled) return options.value;

    const uniqueKey = this.getKey(options.value.name, options.context);

    this.data.set(uniqueKey, options.value);
    return this.data.get(uniqueKey);
  }

  public mark(options: { name: string; context?: any }): AnalyticsEntryMark | undefined {
    if (!this.enabled) return undefined;

    const uniqueKey = this.getKey(options.name, options.context);

    const entry: AnalyticsEntryMark = {
      name: options.name,
      type: 'mark',
      value: perf.now(),
      createdAt: Date.now(),
    };

    this.data.set(uniqueKey, entry);
    return entry;
  }

  public measure(options: {
    name: string;
    start: string;
    end: string;
    context?: any;
    detail?: any;
    sum?: boolean;
  }): AnalyticsEntryMeasure | undefined {
    if (!this.enabled) return undefined;

    const uniqueKey = this.getKey(options.name, options.context);

    const startMark = this.data.get(this.getKey(options.start, options.context));
    const endMark = this.data.get(this.getKey(options.end, options.context));

    if (!startMark || !endMark) {
      return undefined;
    }

    const duration = endMark.value - startMark.value;

    const roundedDuration = Math.round(duration * 100) / 100;

    const existingEntry = this.data.get(uniqueKey);

    if (existingEntry && options.sum) {
      const newDuration = existingEntry.value + roundedDuration;
      const newEntry: AnalyticsEntryMeasure = {
        ...(existingEntry as AnalyticsEntryMeasure),
        value: newDuration,
      };
      this.data.set(uniqueKey, newEntry);
      return newEntry;
    }

    const entry: AnalyticsEntryMeasure = {
      name: options.name,
      type: 'measure',
      value: roundedDuration,
      createdAt: Date.now(),
    };
    if (options.detail) {
      entry.detail = options.detail;
    }

    this.data.set(uniqueKey, entry);
    return entry;
  }

  public update(options: { value: AnalyticsEntry; context?: any }): AnalyticsEntry | undefined {
    if (!this.enabled) return options.value;

    const uniqueKey = this.getKey(options.value.name, options.context);

    const currentItem = this.data.get(uniqueKey);

    if (currentItem) {
      const newItem = { ...currentItem, ...options.value };
      this.data.set(uniqueKey, newItem);
    } else {
      this.data.set(uniqueKey, options.value);
    }
    return this.data.get(uniqueKey);
  }

  public getResourceInfosFromPerformanceAPI(): Array<PerformanceResourceTiming> {
    if (typeof performance === 'undefined') return [];
    const entries = perf.getEntriesByType('resource');
    return entries as Array<PerformanceResourceTiming>;
  }

  public get(options: { name: string; context?: any }): AnalyticsEntry | undefined {
    const uniqueKey = this.getKey(options.name, options.context);

    return this.data.get(uniqueKey);
  }

  public getByType(type: AnalyticsEntryType): Array<AnalyticsEntry> {
    return Array.from(this.data.values()).filter((entry) => entry.type === type);
  }

  public getByName(name: DcuplUpdateType): Array<AnalyticsEntry> {
    return Array.from(this.data.values()).filter((entry) => entry.name === name);
  }

  public clear(): void {
    this.data.clear();
  }

  public getKey(key: string, context: any): string {
    if (!context) {
      return key;
    }

    const hash = generateObjectHash(context);
    return key + '_' + hash;
  }
}
